/**
 * Account & Settings landing — Menu-equivalent for web.
 * Sorted: live items first, stubs second. Visual distinction matches
 * Services/Report/Support catalog pattern (orange border for live).
 * Atlas: Menu tab (web equivalent).
 */
"use client";

import Link from "next/link";
import { Bell, CreditCard, UserCog, MessageSquare, Gauge, Palmtree } from "lucide-react";
import { useActivePersona } from "@/lib/persona";
import { useT } from "@/lib/locale";
import { MENU_STUBS, formatStubStatus } from "@petci/shared-ui";

const MENU_ITEMS = [
  { slug: "account-update", icon: UserCog, color: "text-brand-primary-text", bg: "bg-brand-primary/10", href: "/account/update" },
  { slug: "vacation-mode", icon: Palmtree, color: "text-brand-accent-dark", bg: "bg-brand-accent/10", href: "/account/vacation" },
  { slug: "notifications", icon: Bell, color: "text-brand-accent", bg: "bg-brand-accent/10", href: "/account/notifications" },
  { slug: "autopay", icon: CreditCard, color: "text-brand-secondary", bg: "bg-brand-secondary/10", href: "/account/autopay" },
  { slug: "feedback", icon: MessageSquare, color: "text-semantic-success-text", bg: "bg-semantic-success/10", href: "/account/feedback" },
  { slug: "meter-tools", icon: Gauge, color: "text-neutral-700", bg: "bg-neutral-100", href: "/account/meter-tools" },
];

export default function AccountPage() {
  const persona = useActivePersona();
  const t = useT();

  const items = MENU_ITEMS.flatMap((item) => {
    const stub = MENU_STUBS.find((s) => s.slug === item.slug);
    if (!stub) return [];
    return [{ ...item, stub, live: stub.status.kind === "live" }];
  });

  // Sort: live items first
  const sorted = items.sort((a, b) => {
    if (a.live && !b.live) return -1;
    if (!a.live && b.live) return 1;
    return 0;
  });

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-neutral-900">
          {t("account.title")}
        </h1>
        <p className="text-sm text-neutral-700 mt-1">
          {persona.contactName} · #{persona.accountNumber}
        </p>
      </div>

      <div className="space-y-3">
        {sorted.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.slug}
              href={item.href}
              className={`flex items-center gap-4 rounded-xl px-5 py-4 shadow-sm hover:shadow-card-hover transition-shadow duration-base group ${
                item.live
                  ? "border-2 border-brand-primary bg-brand-primary/5"
                  : "border border-neutral-200 bg-white"
              }`}
            >
              <span className={`w-10 h-10 rounded-lg ${item.bg} flex items-center justify-center flex-shrink-0`}>
                <Icon size={20} className={item.color} aria-hidden />
              </span>
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-semibold text-neutral-900 group-hover:text-brand-primary-text transition-colors duration-fast">
                  {item.stub.nameKey ? t(item.stub.nameKey) : item.stub.name}
                </h2>
                <p className="text-xs text-neutral-500 mt-0.5 truncate">
                  {item.stub.descriptionKey ? t(item.stub.descriptionKey) : item.stub.description}
                </p>
              </div>
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium whitespace-nowrap ${
                item.live
                  ? "bg-semantic-success/10 text-semantic-success-text"
                  : "bg-brand-secondary/10 text-brand-secondary"
              }`}>
                {item.live ? t("stubs.availableNow") : formatStubStatus(item.stub.status, t).label}
              </span>
              <span className="text-neutral-500 text-sm">›</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
